import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/HomePage.css';

interface HomePageProps {
  navigateToHome?: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ navigateToHome }) => {
  const navigate = useNavigate();

  const handleNavigation = (route: string) => {
    try {
      // Scroll to top immediately before navigation
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      navigate(route);
    } catch (error) {
      console.error('HomePage navigation error:', error);
      // Fallback to hash navigation
      window.location.href = route;
    }
  };

  return (
    <div className="home-page">
      <div className="hero-section">
        <div className="hero-badge">
          <span className="badge-icon">✨</span>
          <span className="badge-text">AI-Powered Development Platform</span>
        </div>
        
        <h1 className="hero-title">
          From Idea to Reality
          <span className="hero-highlight"> in Minutes</span>
        </h1>
        
        <p className="hero-subtitle">
          Connect with AI assistants and talented developers to bring your project ideas to life. 
          Get instant help, find collaborators, and launch faster than ever before.
        </p>
      </div>

      <div className="features-section">
        <div className="features-grid">
          <div className="feature-card" onClick={() => handleNavigation('/chat')}>
            <div className="feature-icon">🤖</div>
            <h3>AI Project Assistant</h3>
            <p>Get help defining your project requirements with our intelligent assistant</p>
            <button className="feature-link">Start Assistant →</button>
          </div>
          
          <div className="feature-card" onClick={() => handleNavigation('/developer-chat')}>
            <div className="feature-icon">👨‍💻</div>
            <h3>AI Developer Assistant</h3>
            <p>Get technical guidance and development support from our AI assistant</p>
            <button className="feature-link">Get Support →</button>
          </div>
          
          <div className="feature-card" onClick={() => handleNavigation('/ai-community-member')}>
            <div className="feature-icon">🤖👥</div>
            <h3>AI Community Member Control Center</h3>
            <p>Test and monitor AI Community Member Control Center endpoints and monitoring service</p>
            <button className="feature-link">Test Endpoints →</button>
          </div>
          
          <div className="feature-card" onClick={() => handleNavigation('/proto-hub')}>
            <div className="feature-icon">🚀</div>
            <h3>Proto Hub</h3>
            <p>Explore prototypes and get inspiration for your next project</p>
            <button className="feature-link">Explore Hub →</button>
          </div>
          
          <div className="feature-card" onClick={() => handleNavigation('/pipeline-manager')}>
            <div className="feature-icon">💼</div>
            <h3>Project Listings</h3>
            <p>Browse active projects and opportunities in your pipeline</p>
            <button className="feature-link">Browse Projects →</button>
          </div>
          
          <div className="feature-card" onClick={() => handleNavigation('/developers')}>
            <div className="feature-icon">👥</div>
            <h3>Developer Directory</h3>
            <p>Connect with skilled developers in our community</p>
            <button className="feature-link">Find Developers →</button>
          </div>
          
          <div className="feature-card" onClick={() => handleNavigation('/task-manager')}>
            <div className="feature-icon">📋</div>
            <h3>Task Manager</h3>
            <p>Track and manage tasks assigned to developers with real-time status updates and due date tracking</p>
            <button className="feature-link">Manage Tasks →</button>
          </div>
          
          <div className="feature-card" onClick={() => handleNavigation('/pitch-deck')}>
            <div className="feature-icon">📊</div>
            <h3>Platform Pitch Deck</h3>
            <p>View our comprehensive investor presentation showcasing the platform's vision, features, and market opportunity</p>
            <button className="feature-link">View Pitch Deck →</button>
          </div>
          
          <div className="feature-card" onClick={() => handleNavigation('/landing-page')}>
            <div className="feature-icon">🚀📊</div>
            <h3>Prototype + Pitch Deck Service</h3>
            <p>Transform your tech idea into reality with professional prototype and investor-ready pitch deck. Limited to 10 projects per month.</p>
            <button className="feature-link">Join Waitlist →</button>
          </div>
          
          <div className="feature-card" onClick={() => handleNavigation('/defi-platform')}>
            <div className="feature-icon">💰</div>
            <h3>Tech Project Funding Platform</h3>
            <p>Peer-to-peer DeFi lending specifically for Software, AI, Blockchain, and Automation projects</p>
            <button className="feature-link">Get Funding →</button>
          </div>
          
          <div className="feature-card" onClick={() => handleNavigation('/gamification')}>
            <div className="feature-icon">🎮</div>
            <h3>Developer Gamification</h3>
            <p>Earn points, unlock achievements, and climb the leaderboard by contributing to our platform. Turn your development work into an exciting game!</p>
            <button className="feature-link">Start Playing →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/pipeline-dashboard')}>
            <div className="feature-icon">📊</div>
            <h3>Pipeline Control Center</h3>
            <p>Your mission control for opportunity management and client progression with AI-powered insights</p>
            <button className="feature-link">Open Dashboard →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/user-feedback')}>
            <div className="feature-icon">💬</div>
            <h3>User Testing Feedback Platform</h3>
            <p>Comprehensive feedback collection system with analytics, gamification, and community features. Get insights from every user interaction to improve your platform.</p>
            <button className="feature-link">Give Feedback →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/code-contributions')}>
            <div className="feature-icon">💰🔧</div>
            <h3>Code Contribution Bounties</h3>
            <p>Help develop platform features and earn WLFI tokens. Access our GitHub repository, contribute code, and claim bounties for your contributions.</p>
            <button className="feature-link">Start Contributing →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/coding-education')}>
            <div className="feature-icon">🎓</div>
            <h3>AI Coding Education Course</h3>
            <p>Master full-stack development with 1-on-1 AI guidance. Learn frontend, backend, and deployment with personalized mentorship and real project experience.</p>
            <button className="feature-link">Join Waitlist →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/10Day-Freelance-Kickstart')}>
            <div className="feature-icon">🚀</div>
            <h3>10-Day AI Freelance Kickstart</h3>
            <p>Launch your AI freelance journey in just 10 days. Get hands-on with AI tools, start freelancing smarter, and test-drive GoHighLevel. Only $50.</p>
            <button className="feature-link">Get Started →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/webinar-workshop')}>
            <div className="feature-icon">🎓</div>
            <h3>Live Webinar Workshop</h3>
            <p>Tired of lowball clients and 20% platform fees? Learn the S.T.A.R. Method to our AI-freelancing system that gets you $5K+ clients — fast. Escape Fiverr/Upwork forever.</p>
            <button className="feature-link">Reserve Your Spot →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/blueprint-to-freelance-freedom')}>
            <div className="feature-icon">📖</div>
            <h3>Blueprint To Freelance Freedom</h3>
            <p>Break free from platforms like Fiverr and Upwork. Learn how to land premium clients and build a thriving freelance business using AI systems. Only $29.</p>
            <button className="feature-link">Get Your Copy →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/ai-freelance-accelerator')}>
            <div className="feature-icon">🚀</div>
            <h3>AI Freelance Accelerator Course</h3>
            <p>Scale your freelance business from $5K to $50K+ monthly with advanced strategies, 1-on-1 coaching, and proven AI systems. Complete accelerator program for serious freelancers.</p>
            <button className="feature-link">Enroll Now →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/10Day-Freelance-Kickstart/lesson/1')}>
            <div className="feature-icon">📚</div>
            <h3>Daily Lessons</h3>
            <p>Access your daily lessons with theory videos and practical screen share demonstrations. Each lesson includes worksheets, checklists, and hands-on practice.</p>
            <button className="feature-link">Start Lesson 1 →</button>
          </div>
          
          <div className="feature-card" onClick={() => handleNavigation('/activate-ghl')}>
            <div className="feature-icon">🎯</div>
            <h3>Activate GoHighLevel Trial</h3>
            <p>Complete your 10-Day Kickstart setup by activating your 14-day free GoHighLevel trial. Get full access to CRM, automation, and marketing tools.</p>
            <button className="feature-link">Activate Trial →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/school-of-freelancer-freedom')}>
            <div className="feature-icon">🔗</div>
            <h3>Link in Bio</h3>
            <p>Access all our courses, resources, and social media links in one convenient place. Your gateway to everything XRBlockDev has to offer.</p>
            <button className="feature-link">View Links →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/vibe-coding-bootcamp')}>
            <div className="feature-icon">⚡</div>
            <h3>Vibe Coding Bootcamp</h3>
            <p>Welcome to Software 2.0: From Coder to Orchestrator. Stop writing syntax. Start engineering context. Join the elite bootcamp teaching disciplined frameworks of Vibe Coding.</p>
            <button className="feature-link">Enter Bootcamp →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/ai-fatigue-cure-ebook')}>
            <div className="feature-icon">📚</div>
            <h3>The AI Fatigue Cure E-Book</h3>
            <p>From Tool-Chasing Technician to Systems Architect. Discover the F.I.T.S. Method to eliminate AI Fatigue and transform your freelance business with a stable, integrated ecosystem.</p>
            <button className="feature-link">Read E-Book →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/freelancer-power-library')}>
            <div className="feature-icon">📚⚡</div>
            <h3>Freelancer Power Library</h3>
            <p>Stop the Technician's Nightmare. Access the definitive vault of documented AI systems to escape the Platform Trap and secure Reliable Predictive Monthly (R.P.M.) income.</p>
            <button className="feature-link">Enter Library →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/projects-listing')}>
            <div className="feature-icon">💻</div>
            <h3>Software Project Opportunities</h3>
            <p>Browse available software projects and express your interest. Connect with project owners and find your next development opportunity.</p>
            <button className="feature-link">Browse Projects →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/professional-freelancer-audit-report')}>
            <div className="feature-icon">📊</div>
            <h3>Professional Freelancer Audit Report</h3>
            <p>View your personalized audit report with scaling bottleneck analysis, STAR Method roadmap, and customized learning path to predictable income.</p>
            <button className="feature-link">View Report →</button>
          </div>

          <div className="feature-card" onClick={() => handleNavigation('/buildyoursoftware')}>
            <div className="feature-icon">💻</div>
            <h3>Build Your Software</h3>
            <p>Work with Lamont Evans - Web Developer, Email & CRM Specialist, and XR + Blockchain Strategist. From idea to prototype in 30 minutes.</p>
            <button className="feature-link">Learn More →</button>
          </div>
        </div>
      </div>

      <div className="stats-section">
        <div className="stats-grid">
          <div className="stat-item">
            <div className="stat-number">500+</div>
            <div className="stat-label">Projects Completed</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">200+</div>
            <div className="stat-label">Active Developers</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">98%</div>
            <div className="stat-label">Success Rate</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">24/7</div>
            <div className="stat-label">Support</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
